import templateAPI from '@/api/template.js'
import sentry from '@/services/sentry'
import SearchApi from '@/api/search'

export const TEMPLATE_ACTION = {
  SEARCH: 'SearchTemplates',
  DISCOVER: 'collection:discover',
}
// State structure holds templates and related statuses keyed by dynamic identifiers (e.g., collection, topic)
const _state = () => ({
  itemsByKey: {}, // stores fetched templates by dynamic key
  isLoadingByKey: {}, // loading state for each key
  errorByKey: {}, // error state for each key
})

const _getters = {
  // Get templates by a specific key
  getTemplatesByKey: (state) => (key) => state.itemsByKey[key] || [],

  // Check if data is loading for a specific key
  isTemplatesLoading: (state) => (key) => state.isLoadingByKey[key] || false,

  // Get error message for a specific key
  getTemplatesError: (state) => (key) => state.errorByKey[key] || null,

  // Get discover templates by fixed key
  getDiscoverTemplates: (state) => state.itemsByKey[TEMPLATE_ACTION.DISCOVER] || [],

  // Check if discover templates are loading
  isDiscoverTemplatesLoading: (state) => state.isLoadingByKey[TEMPLATE_ACTION.DISCOVER] || false,

  getSearchTemplates: (state) => (searchValue) => {
    const key = `${TEMPLATE_ACTION.SEARCH}: ${searchValue}`
    return state.itemsByKey[key] || []
  },
}

const actions = {
  // Fetch templates from API based on dynamic key and query parameters
  async fetchTemplatesByKey({ commit }, { key, queryParams }) {
    commit('setLoadingState', { key, value: true })

    try {
      const data = await templateAPI.getTemplates(queryParams)
      commit('setTemplatesData', { key, templates: data })
    } catch (error) {
      commit('setErrorState', { key, error })
      sentry.captureException(error)
    } finally {
      commit('setLoadingState', { key, value: false })
    }
  },

  // Fetch discover templates specifically
  async fetchDiscoverTemplates({ dispatch }) {
    await dispatch('fetchTemplatesByKey', {
      key: TEMPLATE_ACTION.DISCOVER,
      queryParams: { collectionSlug: 'discover' }, // hardcoded for now
    })
  },

  async fetchSearchTemplates({ commit, rootGetters }, query) {
    const useV2 = rootGetters['featureToggles/canUseImprovedTemplateSearch']
    commit('setLoadingState', { key: TEMPLATE_ACTION.SEARCH, value: true })

    try {
      const data = await SearchApi.searchPresentations(query, useV2)

      commit('setTemplatesData', { key: `${TEMPLATE_ACTION.SEARCH}: ${query.q}`, templates: data.body.presentations })
    } catch (error) {
      commit('setErrorState', { key: TEMPLATE_ACTION.SEARCH, error })
      sentry.captureException(error)
    } finally {
      commit('setLoadingState', { key: TEMPLATE_ACTION.SEARCH, value: false })
    }
  },
}

const mutations = {
  // Save templates to the appropriate key
  setTemplatesData(state, { key, templates }) {
    state.itemsByKey = {
      ...state.itemsByKey,
      [key]: templates,
    }
  },

  // Set loading status for a key
  setLoadingState(state, { key, value }) {
    state.isLoadingByKey = {
      ...state.isLoadingByKey,
      [key]: value,
    }
  },

  // Set error message for a key
  setErrorState(state, { key, error }) {
    state.errorByKey = {
      ...state.errorByKey,
      [key]: error,
    }
  },
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}
